import React, { useEffect, useState, useCallback } from "react";
import { Form, Tabs, Modal } from "antd";
import { withRouter, Link, matchPath } from "react-router-dom";
import { ReactSVG } from "react-svg";
import intersection from "lodash/intersection";
import { useDispatch, useSelector } from "react-redux";
import queryString from "query-string";

import RoutePlanForm from "../Forms/RoutePlans/RoutePlan";
import { getTranslation } from "../../utils/helper";
import { doCreateRoutePlan } from "../../modules/routePlan/stores/thunk";
import "./Main.scss";

const DETAIL_ROUTE_PLAN_PATHS = ["/route-plans/:id", "/route-plans/:id/:type"];

const MainMenu = ({ history, user }) => {
  const {
    location: { pathname, search },
  } = history;
  const query = queryString.parse(search);
  const roles = user?.roles || [];
  const setting = useSelector(state => state.setting.setting) || user.company;
  const order = useSelector(state => state.order.item);
  const stateRoutePlan = useSelector(state => state.routePlan);
  const stateRoutePlanMessage = stateRoutePlan?.message;
  const [selectedKey, setSelectedKey] = useState(pathname);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [visibleCreateRPModal, setVisibleCreateRPModal] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const updateSelectedKey = useCallback(
    (newPathname, newQuery, newOrder) => {
      let newSelectedKey = newPathname;

      if (newSelectedKey.includes("route-plans")) {
        newSelectedKey = "/route-plans";
      } else if ((newQuery?.routePlanType || newOrder?.routePlanType) === "VRP") {
        newSelectedKey = "/orders?routePlanType=VRP";
      } else if ((newQuery?.routePlanType || newOrder?.routePlanType) === "PDP") {
        newSelectedKey = "/orders?routePlanType=PDP";
      } else if (newSelectedKey.includes("drivers")) {
        newSelectedKey = "/drivers";
      } else if (newSelectedKey.includes("groups")) {
        newSelectedKey = "/groups";
      } else if (newSelectedKey.includes("users")) {
        newSelectedKey = "/users";
      } else if (newSelectedKey.includes("companies")) {
        newSelectedKey = "/companies";
      } else if (newSelectedKey.includes("company-admins")) {
        newSelectedKey = "/company-admins";
      } else if (newSelectedKey.includes("settings")) {
        newSelectedKey = "/settings";
      } else if (newSelectedKey.includes("pricing")) {
        newSelectedKey = "/pricing";
      } else if (newSelectedKey.includes("message-logs")) {
        newSelectedKey = "/message-logs";
      } else if (newSelectedKey.includes("reports")) {
        newSelectedKey = "/reports";
      } else if (newSelectedKey.includes("partners")) {
        newSelectedKey = "/partners";
      } else {
        newSelectedKey = roles?.includes("user") ? "/orders" : "/route-plans";
      }

      return newSelectedKey;
    },
    [roles],
  );

  useEffect(() => {
    const newKey = updateSelectedKey(pathname, query, order);
    setSelectedKey(newKey);
  }, [pathname, query, order, updateSelectedKey]);

  useEffect(() => {
    if (stateRoutePlanMessage?.includes("create_routeplan_succeeded")) {
      const routePlanId = stateRoutePlanMessage.split("_")[3];
      window.location.href = `/route-plans/${routePlanId}`;
    }
  }, [stateRoutePlanMessage]);

  const handleOpenAddRPModal = () => {
    form.setFieldsValue({ type: setting?.routePlanType });
    setVisibleCreateRPModal(true);
  };

  const handleCloseAddRPModal = () => {
    setVisibleCreateRPModal(false);
    form.resetFields();
  };

  const handleOnCreateRoutePlan = async values => {
    try {
      setIsFetching(true);
      await dispatch(doCreateRoutePlan(values));
    } finally {
      setIsFetching(false);
      handleCloseAddRPModal();
    }
  };

  let isRoutePlanDetailPage = false;
  for (const detailPath of DETAIL_ROUTE_PLAN_PATHS) {
    const match = matchPath(pathname, {
      path: detailPath,
      exact: true,
      strict: true,
    });
    if (match) {
      isRoutePlanDetailPage = true;
      break;
    }
  }

  if (!user) return null;

  const menuItems = [];

  if (roles?.includes("user")) {
    menuItems.push({
      key: "/orders",
      label: (
        <>
          <div className="MenuItemTop">
            <ReactSVG className="icon" src="/icons/orders.svg" />
          </div>
          <div className="MenuItemContent">
            <Link to="/orders">{getTranslation(setting, "Menu", "Orders")}</Link>
          </div>
        </>
      ),
    });
  }

  if (intersection(["company_admin", "admin", "partner"], roles).length > 0) {
    if (!roles.includes("partner")) {
      menuItems.push({
        key: "/route-plans",
        label: (
          <>
            <div className="MenuItemTop">
              <ReactSVG className="icon" src="/icons/route-plans.svg" />
              {isRoutePlanDetailPage && (
                <ReactSVG onClick={handleOpenAddRPModal} className="icon add-icon" src="/icons/add_circle.svg" />
              )}
            </div>
            <div className="MenuItemContent">
              <Link to="/route-plans">{getTranslation(setting, "Menu", "Route Plans")}</Link>
            </div>
          </>
        ),
      });

      if (!isRoutePlanDetailPage) {
        menuItems.push(
          {
            key: "/orders?routePlanType=VRP",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/vrp.svg" />
                  Single Stop
                </div>
                <div className="MenuItemContent">
                  <Link to="/orders?routePlanType=VRP">{getTranslation(setting, "Menu", "Orders")}</Link>
                </div>
              </>
            ),
          },
          {
            key: "/orders?routePlanType=PDP",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/pdp.svg" />
                  Pickup & Delivery
                </div>
                <div className="MenuItemContent">
                  <Link to="/orders?routePlanType=PDP">{getTranslation(setting, "Menu", "Orders")}</Link>
                </div>
              </>
            ),
          },
          {
            key: "/drivers",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/drivers.svg" />
                </div>
                <div className="MenuItemContent">
                  <Link to="/drivers">{getTranslation(setting, "Menu", "Drivers")}</Link>
                </div>
              </>
            ),
          },
          {
            key: "/groups",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/groups.svg" />
                </div>
                <div className="MenuItemContent">
                  <Link to="/groups">{getTranslation(setting, "Menu", "User Groups")}</Link>
                </div>
              </>
            ),
          },
          {
            key: "/users",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/users.svg" />
                </div>
                <div className="MenuItemContent">
                  <Link to="/users">{getTranslation(setting, "Menu", "Users")}</Link>
                </div>
              </>
            ),
          },
          {
            key: "/reports",
            label: (
              <>
                <div className="MenuItemTop">
                  <ReactSVG className="icon" src="/icons/receipt.svg" />
                </div>
                <div className="MenuItemContent">
                  <Link to="/reports">{getTranslation(setting, "Menu", "Reports")}</Link>
                </div>
              </>
            ),
          },
        );
      }
    }

    if (roles.includes("admin")) {
      menuItems.push(
        {
          key: "/companies",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/companies.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/companies">{getTranslation(setting, "Menu", "Companies")}</Link>
              </div>
            </>
          ),
        },
        {
          key: "/company-admins",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/company-admins.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/company-admins">{getTranslation(setting, "Menu", "Comp Admins")}</Link>
              </div>
            </>
          ),
        },
        {
          key: "/partners",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/partner.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/partners">{getTranslation(setting, "Menu", "Partners")}</Link>
              </div>
            </>
          ),
        },
      );
    }

    if (roles.includes("partner")) {
      menuItems.push(
        {
          key: "/companies",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/companies.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/partner/companies">{getTranslation(setting, "Menu", "Sub Account")}</Link>
              </div>
            </>
          ),
        },
        {
          key: "/settings",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/setting.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/settings">{getTranslation(setting, "Menu", "Settings")}</Link>
              </div>
            </>
          ),
        },
      );
    }

    if (setting?.status === "ASKING_FOR_PURCHASE") {
      menuItems.push({
        key: "/pricing",
        label: (
          <>
            <div className="MenuItemTop">
              <ReactSVG className="icon" src="/icons/receipt.svg" />
            </div>
            <div className="MenuItemContent">
              <a href="https://www.parcelizer.com/pricing">{getTranslation(setting, "Menu", "Pricing")}</a>
            </div>
          </>
        ),
      });
    }

    if (roles.includes("company_admin")) {
      menuItems.push(
        {
          key: "/settings",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/setting.svg" />
              </div>
              <div className="MenuItemContent">
                <Link to="/settings">{getTranslation(setting, "Menu", "Settings")}</Link>
              </div>
            </>
          ),
        },
        {
          key: "/docs",
          label: (
            <>
              <div className="MenuItemTop">
                <ReactSVG className="icon" src="/icons/docs.svg" />
              </div>
              <div className="MenuItemContent">
                <a href="https://intercom.help/parcelizer/en" target="_blank" rel="noreferrer">
                  {getTranslation(setting, "Menu", "Docs")}
                </a>
              </div>
            </>
          ),
        },
      );
    }
  }

  return (
    <>
      <Tabs
        className={`MainMenu ${setting?.status === "ASKING_FOR_PURCHASE" ? "has-pricing" : ""}`}
        activeKey={selectedKey}
        items={menuItems.map(item => ({
          key: item.key,
          label: (
            <div
              onClick={() => {
                if (item.key === "/docs") {
                  window.open("https://intercom.help/parcelizer/en", "_blank", "noopener,noreferrer");
                  return;
                }
                setSelectedKey(item.key);
                window.location.href = item.key;
              }}
              style={{ cursor: "pointer", height: "100%", width: "100%" }}>
              {item.label}
            </div>
          ),
        }))}
        onChange={key => {
          if (key !== selectedKey) {
            setSelectedKey(key);
          }
        }}
        style={{ flex: 1 }}
        type="line"
        size="large"
      />
      <Modal
        title={getTranslation(setting, "Route Plans", "Create Route Plan")}
        open={visibleCreateRPModal}
        onCancel={handleCloseAddRPModal}
        footer={null}>
        <RoutePlanForm form={form} onFinish={handleOnCreateRoutePlan} isFetching={isFetching} setting={setting} />
      </Modal>
    </>
  );
};

export default withRouter(MainMenu);
